/**
 * SweepstakesDataMapper.js
 * Transforms API response structure to component-friendly format
 */
import { SWEEPSTAKES_CONSTANTS } from '../../../core/Constants';

/**
 * Calculate status badge text from progress
 * @param {number} completed - Number of completed steps
 * @param {number} total - Total number of steps
 * @param {boolean} isOptedIn - Whether user is opted in
 * @returns {string} Status text for badge
 */
export const calculateStatus = (completed = 0, total = 0, isOptedIn = false) => {
  if (!isOptedIn || completed === 0) return 'New';
  return `${completed} of ${total} Earned`;
};

/**
 * Safely parse metaData that may be a JSON string (real API) or object (mock)
 * @param {*} meta - metaData value from task
 * @returns {Object|null} Parsed metadata object
 */
const parseTaskMetaData = (meta) => {
  if (!meta) return null;
  if (Array.isArray(meta)) {
    return meta.reduce((accumulator, item) => {
      if (!item?.key) return accumulator;
      accumulator[item.key] = item.value;
      return accumulator;
    }, {});
  }
  if (typeof meta === 'object') return meta;
  if (typeof meta === 'string') {
    try {
      const parsedMeta = JSON.parse(meta);
      if (Array.isArray(parsedMeta)) {
        return parsedMeta.reduce((accumulator, item) => {
          if (!item?.key) return accumulator;
          accumulator[item.key] = item.value;
          return accumulator;
        }, {});
      }
      return parsedMeta;
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Map API task to component step format
 * @param {Object} task - Task from API response
 * @param {string} globalIcon - Global step tracker icon URL
 * @param {number} index - Zero-based position of the task in the steps array
 * @returns {Object} Component-friendly step object
 */
const mapTaskToStep = (task, globalIcon, index) => {
  if (!task) return null;

  const rawMeta = task.taskMetaData || task.stepMetaData || task.metaData || null;
  const stepMetaData = parseTaskMetaData(rawMeta);
  const ctaHref = stepMetaData?.ctaUrl || stepMetaData?.surveyUrl || null;
  const ctaTitle = stepMetaData?.ctaDescription || task.name || null;
  const eventBusTrigger = stepMetaData?.ctaAction || task.eventBusTrigger || null;

  return {
    id: task.customerTaskId || task.name,
    customerChallengeStepId: task.customerChallengeStepId || null,
    stepIndex: index,
    title: task.name,
    subtitle: stepMetaData?.subTitle || null,
    description: stepMetaData?.stepDescription || task.description || null,
    iconUrl: globalIcon || 'https://placehold.co/32x32/000000/FFFFFF?text=%23',
    completed: task.status === 'COMPLETED',
    ctaTitle,
    ctaHref,
    eventBusTrigger,
    brightcoveVideoId: stepMetaData?.brightcoveVideoId || null,
    stepMetaData,
  };
};

/**
 * Format an ISO date string (YYYY-MM-DD) to MM/DD/YYYY for display.
 * Uses string splitting to avoid timezone offset issues with date-only strings.
 * Falls back to locale formatting for non-ISO formats.
 * @param {string} dateStr
 * @returns {string|null}
 */
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = String(dateStr).split('-');
  if (parts.length === 3 && parts[0].length === 4) {
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    const year = parts[0].slice(2);
    return `${month}/${day}/${year}`;
  }
  const parsed = new Date(dateStr);
  return !Number.isNaN(parsed.getTime())
    ? parsed.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })
    : null;
};

/**
 * Map API response to SweepstakesEntryCard props
 * @param {Object} apiResponse - Raw API response
 * @returns {Object} Component-friendly data structure
 */
export const mapSweepstakesData = (apiResponse) => {
  if (!apiResponse) return null;

  const {
    challengeId,
    customerChallengeId,
    v2Challenge,
    name,
    description,
    imageUrl,
    endDate,
    termsAndConditions,
    optedStatus,
    activityStatus,
    challengeStatus,
    challengeStepsCompleted,
    challengeStepsTotal,
    tasks,
    challengeMetaData,
  } = apiResponse;
  // Convert challengeMetaData to object format
  // API may return as Map scalar, array, or already as object
  // Convert to object: {type: "SWEEPSTAKES", ...}
  let metadata = {};

  if (Array.isArray(challengeMetaData)) {
    // Legacy format: [{key: "type", value: "SWEEPSTAKES"}, ...]
    metadata = challengeMetaData.reduce((acc, item) => {
      acc[item.key] = item.value;
      return acc;
    }, {});
  } else if (typeof challengeMetaData === 'object' && challengeMetaData !== null) {
    // Object format (mock data or already processed)
    metadata = challengeMetaData;
  } else if (typeof challengeMetaData === 'string') {
    // Handle potential JSON string from Map scalar
    try {
      metadata = JSON.parse(challengeMetaData);
    } catch {
      metadata = {};
    }
  } else {
    metadata = {};
  }

  const isOptedIn = optedStatus === 'OPTED_IN';

  // Extract and sort checkbox keys once
  const checkboxKeys = Object.keys(metadata)
    .filter((key) => key.startsWith('preOptInDrawer_checkbox_'))
    .sort((aKey, bKey) => {
      const numA = parseInt(aKey.split('_').pop(), 10);
      const numB = parseInt(bKey.split('_').pop(), 10);
      return numA - numB;
    });

  // Extract all checkbox values
  const optInCheckboxes = checkboxKeys.map((key) => metadata[key]);

  // Separate required checkboxes (exclude checkbox_1 which is for email marketing consent)
  const requiredCheckboxIndices = checkboxKeys
    .filter((key) => !key.endsWith('_1'))
    .map((key) => {
      const checkboxNum = parseInt(key.split('_').pop(), 10);
      return checkboxNum - 1;
    });

  // Map tasks to steps with null checking
  const mappedSteps = tasks && Array.isArray(tasks)
    ? tasks.map((task, index) => mapTaskToStep(task, metadata.stepTrackerIcon, index)).filter(Boolean)
    : [];

  // Extract and sort prize keys (preOptInDrawer_prize_1, _2, etc.)
  const prizeKeys = Object.keys(metadata)
    .filter((key) => key.startsWith('preOptInDrawer_prize_'))
    .sort((aKey, bKey) => {
      const numA = parseInt(aKey.split('_').pop(), 10);
      const numB = parseInt(bKey.split('_').pop(), 10);
      return numA - numB;
    });
  const optInPrizes = prizeKeys.map((key) => metadata[key]);

  return {
    // Core identifiers
    challengeId,
    id: challengeId || customerChallengeId,
    customerChallengeId,
    isV2: v2Challenge,

    // Display content
    title: name,
    drawerDescription: description,
    completedDrawerDescription: metadata.sweepstakesDrawer_completed_description,
    cardDescription: metadata.sweepstakesCard_description,
    logo: imageUrl,
    heroImage: metadata.preOptInBannerImage
      || SWEEPSTAKES_CONSTANTS.URLS.BANNER_FALLBACK,

    // Status and progress
    status: calculateStatus(challengeStepsCompleted, challengeStepsTotal, isOptedIn),
    challengeType: metadata.typeOfChallenge || metadata.type || null,
    isOptedIn,
    activityStatus: activityStatus || null,
    challengeStatus: challengeStatus || null,
    completedSteps: challengeStepsCompleted || 0,
    totalSteps: challengeStepsTotal || 0,

    // Date information
    expirationDate: endDate,

    // Bonus entry fields
    bonusEntryEligible: metadata.bonusEntryEligible === 'true',
    messageComplete: metadata.bonusEntry_postText,
    messageNotComplete: metadata.bonusEntry_preText,
    bonusEntryImage: metadata.bonusEntry_img,

    // Steps/tasks
    steps: mappedSteps,
    stepTrackerIcon: metadata.stepTrackerIcon,
    inactiveStepTrackerIcon: metadata.inactiveStepTrackerIcon || null,

    // Terms and drawer content
    termsAndConditions,

    // Opt-in drawer content
    preOptInDrawerTitle: metadata.preOptInDrawer_title,
    preOptInDrawerBackgroundImage: metadata.preOptInDrawer_backgroundImage,
    preOptInDrawerHeaderLine1: metadata.preOptInDrawer_headerLine1,
    preOptInDrawerHeaderLine2: metadata.preOptInDrawer_headerLine2,
    preOptInDrawerDescription: metadata.preOptInDrawer_description,
    preOptInDrawerPrizesHeading: metadata.preOptInDrawer_prizesHeading || 'You Could Win',
    optInPrizes,
    postOptInDrawerDescription: metadata.postOptInDrawer_description,
    preOptInDrawerCta: metadata.preOptInDrawer_cta,
    optInCheckboxes,
    requiredCheckboxIndices,

    // Details drawer content
    stepTrackerLearnMoreText: metadata.stepTracker_learnMore_text || metadata.stepTracker_leanrMore_text,
    stepTrackerPostOptInDescription: metadata.stepTracker_postOptIn_preRequistie_drawer_description,
    allEntriesCompletedText: metadata.all_entries_completed_text,
    videoInfoText: metadata.info_text || null,

    // Progress bar CTA labels
    inProgressCta: metadata.inProgress_cta || 'Earn More Entries',
    completedCta: metadata.completed_cta || 'Review Entries',
    getStartedCta: metadata.welcomeCard_getStarted_cta || 'Get Started',
    inProgressCardDescription: metadata.sweepstakesCard_inProgress_description || null,
    completedCardDescription: metadata.sweepstakesCard_completed_description || null,

    // Ended state
    sweepstakesEndedDescription: metadata.sweepstakesEndedDescription || null,
    displayChallengeUntil: formatDisplayDate(metadata.display_challenge_until),

    // Additional metadata
    sweepstakesFaqs: metadata['Sweeps FAQ\'s'],
    welcomeCardTitle: metadata.welcomeCard_title,

    // CTA information (calculated)
    ctaTitle: isOptedIn ? null : (metadata.preOptInDrawer_cta || 'Take Survey'),
    ctaHref: null,
    ctaOnclick: null,
  };
};

/**
 * Map multiple sweepstakes from API response
 * @param {Object} apiResponse - API response with challenges array
 * @returns {Array} Array of mapped sweepstakes data
 */
export const mapSweepstakesArray = (apiResponse) => {
  if (!apiResponse || !apiResponse.challenges) return [];

  return apiResponse.challenges
    .map((challenge) => mapSweepstakesData(challenge))
    .filter(Boolean);
};

/**
 * Get first active sweepstakes from API response
 * @param {Object} apiResponse - API response with challenges array
 * @returns {Object|null} First active sweepstakes or null
 */
export const getFirstActiveSweepstakes = (apiResponse) => {
  const mapped = mapSweepstakesArray(apiResponse);
  return mapped.length > 0 ? mapped[0] : null;
};
